/**
 * Starting an inspection.
 *
 * Every field is optional except the address, and that is a deliberate product decision
 * rather than laziness about validation. A rep pulling into a driveway in hail season knows
 * the address; they may not have the claim number until the homeowner finds the letter. A
 * form that refuses to start until every box is filled gets worked around by typing "x" in
 * the boxes, and then the record is worse than if we had let the field stay empty. Missing
 * data is visibly missing. Fabricated data is not.
 *
 * No network, no spinner, no failure path. `createJob` writes the row and its op in one
 * local transaction, so this submits identically on wifi and in a basement.
 */

import { useState } from 'react';

import { INSPECTION_TEMPLATES, type InspectionKind } from '@rush/sync-core';

import type { NewJobInput } from '../db/repository.js';
import { styles } from './styles.js';

const KINDS = Object.values(INSPECTION_TEMPLATES);

export function NewJobScreen({
  onCreate,
  onCancel,
}: {
  onCreate: (input: NewJobInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<InspectionKind>('roof');
  const [address, setAddress] = useState('');
  const [homeownerName, setHomeownerName] = useState('');
  const [homeownerPhone, setHomeownerPhone] = useState('');
  const [dateOfLoss, setDateOfLoss] = useState('');
  const [carrier, setCarrier] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const submit = () => {
    if (address.trim() === '' || busy) return;
    setBusy(true);
    setError(undefined);

    onCreate({
      kind,
      address: address.trim(),
      homeownerName: homeownerName.trim(),
      homeownerPhone: homeownerPhone.trim(),
      dateOfLoss,
      carrier: carrier.trim(),
      claimNumber: claimNumber.trim(),
    })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <label style={styles.label}>
        Inspection type
        <select
          style={styles.input}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as InspectionKind);
          }}
        >
          {KINDS.map((template) => (
            <option key={template.kind} value={template.kind}>
              {template.label}
            </option>
          ))}
        </select>
      </label>

      <Field label="Property address" value={address} onChange={setAddress} autoFocus />
      <Field label="Homeowner name" value={homeownerName} onChange={setHomeownerName} />
      <Field label="Homeowner phone" value={homeownerPhone} onChange={setHomeownerPhone} type="tel" />
      <Field label="Date of loss" value={dateOfLoss} onChange={setDateOfLoss} type="date" />
      <Field label="Carrier" value={carrier} onChange={setCarrier} />
      <Field label="Claim number" value={claimNumber} onChange={setClaimNumber} />

      {error !== undefined && <p style={{ ...styles.cardMeta, color: '#ff9c9c' }}>{error}</p>}

      <button
        type="button"
        style={{
          ...styles.button,
          ...styles.buttonPrimary,
          ...(address.trim() === '' ? { opacity: 0.5 } : {}),
        }}
        disabled={address.trim() === '' || busy}
        onClick={submit}
        data-testid="create-job"
      >
        {busy ? 'Starting…' : 'Start inspection'}
      </button>

      <button type="button" style={styles.button} onClick={onCancel}>
        Cancel
      </button>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: string;
  autoFocus?: boolean;
}) {
  return (
    <label style={styles.label}>
      {label}
      <input
        style={styles.input}
        type={type}
        value={value}
        autoFocus={autoFocus}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </label>
  );
}
